import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import MenuManagement from './pages/MenuManagement';
import CategoryManagement from './pages/CategoryManagement';
import TableManagement from './pages/TableManagement';
import OrderManagement from './pages/OrderManagement';
import KitchenDisplay from './pages/KitchenDisplay';
import QRCodeGenerator from './pages/QRCodeGenerator';
import Checkout from './pages/Checkout';
import TransactionHistory from './pages/TransactionHistory';
import DailySummary from './pages/DailySummary';
import SalesByCategory from './pages/SalesByCategory';
import Promotions from './pages/Promotions';
import CustomerCardManagement from './pages/CustomerCardManagement';
import StockManagement from './pages/StockManagement';
import StockCount from './pages/StockCount';
import EmployeeManagement from './pages/EmployeeManagement';
import StockHistory from './pages/StockHistory';
import CostReport from './pages/CostReport';
import SyncStatus from './pages/SyncStatus';
import BuffetPricing from './pages/BuffetPricing';
import PrinterSettings from './pages/PrinterSettings';
import BranchManagement from './pages/BranchManagement';
import CashDrawer from './pages/CashDrawer';
import Login from './pages/Login';
import { useOnlineStatus } from './hooks/useOfflineSync';
import { loadPricing } from './utils/pricing.js';
import { BranchProvider } from './contexts/BranchContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import BranchSwitcher from './components/BranchSwitcher';
import AllBranchesBanner from './components/AllBranchesBanner';
import BottomNav from './components/BottomNav';
import ShiftLandingRedirect, { SHIFT_LANDING_KEY } from './components/ShiftLandingRedirect';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const PRINTER_URL = import.meta.env.VITE_PRINTER_URL || API_URL;

const SIDEBAR_GROUPS = [
  {
    key: 'daily',
    label: 'การขายประจำวัน',
    defaultOpen: true,
    items: [
      { to: '/tables', icon: '🪑', label: 'จัดการโต๊ะ' },
      { to: '/orders', icon: '📝', label: 'รายการสั่งอาหาร' },
      { to: '/kitchen', icon: '👨‍🍳', label: 'จอครัว' },
      { to: '/checkout', icon: '💰', label: 'ชำระเงิน' },
      { to: '/cash-drawer', icon: '💵', label: 'เงินทอน/ลิ้นชัก' },
    ]
  },
  {
    key: 'reports',
    label: 'รายงาน',
    defaultOpen: false,
    items: [
      { to: '/transaction-history', icon: '📋', label: 'ประวัติการขาย' },
      { to: '/daily-summary', icon: '📅', label: 'สรุปยอดประจำวัน' },
      { to: '/sales-by-category', icon: '📂', label: 'ยอดขายตามประเภท' },
    ]
  },
  {
    key: 'manage',
    label: 'จัดการร้าน',
    defaultOpen: false,
    items: [
      { to: '/categories', icon: '📂', label: 'จัดการหมวดหมู่' },
      { to: '/menu', icon: '🍽️', label: 'จัดการเมนู' },
      { to: '/promotions', icon: '🎁', label: 'จัดการโปรโมชั่น' },
      { to: '/customer-cards', icon: '💳', label: 'การ์ดหน้าลูกค้า' },
      { to: '/employees', icon: '👥', label: 'จัดการบุคลากร' },
      { to: '/stock', icon: '📦', label: 'จัดการสต็อก' },
    ]
  },
  {
    key: 'settings',
    label: 'ตั้งค่า',
    defaultOpen: false,
    items: [
      { to: '/branches', icon: '🏬', label: 'จัดการสาขา' },
      { to: '/buffet-pricing', icon: '💰', label: 'ตั้งราคาบุฟเฟ่ต์' },
      { to: '/printer-settings', icon: '🖨️', label: 'ตั้งค่าเครื่องพิมพ์' },
      { to: '/qr-codes', icon: '📱', label: 'สร้าง QR Code' },
    ]
  }
];

function SidebarGroup({ group, onNavigate }) {
  const [open, setOpen] = useState(group.defaultOpen);
  return (
    <>
      <div className="sidebar-group-header" onClick={() => setOpen(o => !o)}>
        <span className="sidebar-group-label">{group.label}</span>
        <span className={`sidebar-group-arrow ${open ? 'open' : ''}`}>▶</span>
      </div>
      <div className="sidebar-group-items" style={{ maxHeight: open ? '500px' : '0' }}>
        {group.items.map(item => (
          <li key={item.to}>
            <NavLink to={item.to} onClick={onNavigate}>
              <span style={{ marginRight: '0.7rem' }}>{item.icon}</span>{item.label}
            </NavLink>
          </li>
        ))}
      </div>
    </>
  );
}

// /transactions ("รายงานการขาย") ถูกรวมเข้ากับ /transaction-history ("ประวัติการขาย")
// แล้ว — คงไว้แค่ redirect กัน bookmark/ลิงก์เก่าพัง คง query string ไว้ด้วย (เช่น
// ?filter=today จาก Dashboard.jsx) ให้ TransactionHistory.jsx อ่านต่อได้ทันที
function TransactionsRedirect() {
  const location = useLocation();
  return <Navigate to={`/transaction-history${location.search}`} replace />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  // เดสก์ท็อป (>768px) sidebar เป็นคอลัมน์ static เปิดค้างเสมออยู่แล้วจาก CSS ไม่สนใจ state นี้
  // แต่จอเล็ก (<=768px) .sidebar กลายเป็น drawer แบบ fixed+overlay ที่ผูกกับ state ตัวนี้ตรงๆ
  // — ถ้าเริ่มที่ true เหมือนเดิมเสมอ มือถือ/แท็บเล็ตจะเจอเมนูเต็มจอบัง content ทันทีที่โหลด
  // หน้าเข้ามาทุกครั้ง ต้องแตะพื้นมืดปิดเองก่อนถึงจะเห็นหน้าจริง ต้อง sync กับ breakpoint เดียวกับ
  // index.css (`@media (max-width: 768px)`) ตรงนี้
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth > 768 : true
  );
  // When running inside the Electron desktop app, printer status/config calls must go
  // through the LOCAL API (localhost) — the cloud API on Railway cannot TCP-connect to
  // printers on the branch's local network.
  const [printerApiUrl, setPrinterApiUrl] = useState(PRINTER_URL);
  const isOnline = useOnlineStatus();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) setIsAuthenticated(true);
    loadPricing(API_URL).finally(() => setLoading(false));
    // Resolve local API URL if running inside Electron
    if (window.desktop?.getLocalApiUrl) {
      window.desktop.getLocalApiUrl()
        .then(url => { if (url) setPrinterApiUrl(url); })
        .catch(() => {});
    }
  }, []);

  // user คือ { username, role, branchId? } จาก POST /admin/login — role: 'manager'
  // หมายถึงบัญชีผู้จัดการสาขา (ล็อกอินด้วย username/password เดียวกับแอปพนักงาน)
  // เข้าแอดมินได้เหมือนกันแต่จำกัดเฉพาะสาขาตัวเอง ต่างจากเจ้าของร้าน (role: 'admin')
  const handleLogin = (token, user) => {
    localStorage.setItem('adminToken', token);
    // 'manager' และ 'employee' ล็อกอินหน้านี้ได้ทั้งคู่แล้ว (ดู admin.route.js) ต่างกันแค่สิทธิ์
    // ที่ backend กันไว้ (requireManagerOrOwner) — ฝั่งนี้ล็อกสาขาเหมือนกันทั้งสอง role
    if ((user?.role === 'manager' || user?.role === 'employee') && user.branchId) {
      localStorage.setItem('adminRole', user.role);
      localStorage.setItem('adminManagerBranchId', user.branchId);
    } else {
      localStorage.removeItem('adminRole');
      localStorage.removeItem('adminManagerBranchId');
    }
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminManagerBranchId');
    // ล็อกอินครั้งถัดไปต้องถูกพาไปหน้าเปิดร้านอีกรอบ (ถ้ายังไม่เปิดกะ)
    try { sessionStorage.removeItem(SHIFT_LANDING_KEY); } catch { /* ไม่เป็นไร */ }
    setIsAuthenticated(false);
  };

  const isBranchManager = localStorage.getItem('adminRole') === 'manager';
  // พนักงานธรรมดา (role: 'employee') ล็อกอินหน้านี้ได้แล้ว แต่ backend กันสิทธิ์ไว้แค่งาน
  // หน้าร้านประจำวัน (requireManagerOrOwner บล็อกที่เหลือ) — เมนูฝั่งนี้ต้องเหลือแค่กลุ่ม
  // "การขายประจำวัน" ให้ตรงกัน ไม่งั้นจะเห็นลิงก์ที่กดแล้วเจอ 403 เต็มไปหมด
  const isEmployeeTier = localStorage.getItem('adminRole') === 'employee';
  // จัดการสาขา (สร้าง/แก้/ลบสาขาเอง) เป็นของเจ้าของร้านเท่านั้น — backend บล็อกไว้แล้ว
  // (requireOwnerAdmin) ซ่อนออกจากเมนูฝั่งผู้จัดการด้วย จะได้ไม่เห็นลิงก์ที่กดแล้ว 403
  const visibleSidebarGroups = isEmployeeTier
    ? SIDEBAR_GROUPS.filter(group => group.key === 'daily')
    : isBranchManager
      ? SIDEBAR_GROUPS.map(group => ({ ...group, items: group.items.filter(item => item.to !== '/branches') }))
      : SIDEBAR_GROUPS;

  if (loading) {
    return <div className="loading">กำลังโหลด...</div>;
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} apiUrl={API_URL} />;
  }

  return (
    <BranchProvider apiUrl={API_URL}>
    <ConfirmProvider>
    <BrowserRouter>
      <ShiftLandingRedirect apiUrl={API_URL} />
      <div className="admin-layout">
        {/* Sidebar Overlay for mobile — เปิดเมนูผ่านปุ่ม "เมนู" ใน BottomNav (ไม่มีปุ่มแฮมเบอร์เกอร์ลอยแล้ว) */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <nav className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <img src="/logo.jpg" alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            <span>
              ชาบู ฮิดะ ปิ้งย่าง
              <small>ระบบจัดการร้าน</small>
            </span>
          </h1>
          {/* ผู้จัดการสาขาถูกล็อกไว้สาขาเดียวเสมอ (ดู BranchContext) — branches มีแค่ 1 รายการ
              BranchSwitcher เลยแสดงแค่ชื่อสาขาเฉยๆ ไม่มี dropdown ให้สลับอยู่แล้วโดยธรรมชาติ */}
          <BranchSwitcher />
          <ul>
            {!isEmployeeTier && (
              <li>
                <NavLink to="/" end onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}>
                  <span style={{ marginRight: '0.7rem' }}>📊</span>แดชบอร์ด
                </NavLink>
              </li>
            )}
            {visibleSidebarGroups.map(group => (
              <SidebarGroup
                key={group.key}
                group={group}
                onNavigate={() => window.innerWidth < 768 && setSidebarOpen(false)}
              />
            ))}
          </ul>
          <button onClick={handleLogout} className="logout-btn">
            <span style={{ marginRight: '0.5rem' }}>🚪</span>ออกจากระบบ
          </button>
        </nav>

        <main className="content">
          <AllBranchesBanner />

          {/* Global Offline Banner */}
          {!isOnline && (
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '10px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              <span>📵</span>
              <span>ออฟไลน์ - ข้อมูลจะซิงค์อัตโนมัติเมื่อออนไลน์</span>
            </div>
          )}

          <Routes>
            <Route path="/" element={<Dashboard apiUrl={API_URL} printerUrl={printerApiUrl} />} />
            <Route path="/tables" element={<TableManagement apiUrl={API_URL} />} />
            <Route path="/categories" element={<CategoryManagement apiUrl={API_URL} />} />
            <Route path="/menu" element={<MenuManagement apiUrl={API_URL} />} />
            <Route path="/orders" element={<OrderManagement apiUrl={API_URL} />} />
            <Route path="/kitchen" element={<KitchenDisplay apiUrl={API_URL} />} />
            <Route path="/qr-codes" element={<QRCodeGenerator apiUrl={API_URL} />} />
            <Route path="/checkout" element={<Checkout apiUrl={API_URL} printerUrl={printerApiUrl} />} />
            <Route path="/transactions" element={<TransactionsRedirect />} />
            <Route path="/transaction-history" element={<TransactionHistory apiUrl={API_URL} />} />
            <Route path="/daily-summary" element={<DailySummary apiUrl={API_URL} />} />
            <Route path="/sales-by-category" element={<SalesByCategory apiUrl={API_URL} />} />
            <Route path="/promotions" element={<Promotions apiUrl={API_URL} />} />
            <Route path="/customer-cards" element={<CustomerCardManagement apiUrl={API_URL} />} />
            <Route path="/stock" element={<StockManagement apiUrl={API_URL} />} />
            <Route path="/stock/history" element={<StockHistory apiUrl={API_URL} />} />
            <Route path="/stock/report" element={<CostReport apiUrl={API_URL} />} />
            <Route path="/stock/count" element={<StockCount apiUrl={API_URL} />} />
            <Route path="/employees" element={<EmployeeManagement apiUrl={API_URL} />} />
            <Route path="/branches" element={<BranchManagement apiUrl={API_URL} />} />
            <Route path="/buffet-pricing" element={<BuffetPricing apiUrl={API_URL} />} />
            <Route path="/printer-settings" element={<PrinterSettings apiUrl={API_URL} printerApiUrl={printerApiUrl} />} />
            <Route path="/cash-drawer" element={<CashDrawer apiUrl={API_URL} />} />
            <Route path="/sync-status" element={<SyncStatus apiUrl={API_URL} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>

        <BottomNav onOpenMenu={() => setSidebarOpen(true)} />
      </div>
    </BrowserRouter>
    </ConfirmProvider>
    </BranchProvider>
  );
}

export default App;
